import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box } from '@mui/material';
import { getAllFundraiser } from 'src/shared/services/fundraiserService';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { FundraiserTypeEnumLabels } from 'src/shared/models/enums/fundraiserType.enum';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { FundraiserSearchFilter } from '../models/fundraiserSearchFilter';
import { FundraiserResponse } from '../models/fundraiserResponse';

type FundraiserDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: FundraiserSearchFilter;
  searchFilterCallback: (filter: FundraiserSearchFilter) => void;
};

interface DisplayFundraiserResponse extends FundraiserResponse {
  id: number;
}

export const FundraiserSettingsDataGrid = (props: FundraiserDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['fundraiser']);
  const isMounted = useIsMounted();

  const navigate = useNavigate();

  const [rows, setRows] = useState<DisplayFundraiserResponse[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageSize,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllFundraiser(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.fundraiser_id };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.FundraiserSettingsDetail}/${params.row.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'heading',
      headerName: t('fundraiser:tableHeader.name'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.heading}</>;
      },
    },
    {
      field: 'type',
      headerName: t('fundraiser:tableHeader.type'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{FundraiserTypeEnumLabels(params.row.type, t)}</>;
      },
    },
    {
      field: 'createdDate',
      headerName: t('fundraiser:tableHeader.dateCreated'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdDate) || ''}</>;
      },
    },
    {
      field: 'fundraiser_id',
      headerName: t('fundraiser:tableHeader.dateCreated'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdDate) || ''}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
    >
      <DataGrid<DisplayFundraiserResponse>
        columns={columns}
        rows={rows}
        autoHeight
        columnVisibilityModel={{
          fundraiser_id: false,
        }}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel);
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        onRowClick={onRowClick}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
